import { useAuthStore } from '@features/auth/store/authStore';
import { doc, serverTimestamp, setDoc } from 'firebase/firestore';
import { useEffect, useRef } from 'react';
import { db } from '../../../config/firebase';

const PRESENCE_COLLECTION = 'presence';
const HEARTBEAT_INTERVAL_MS = 25_000;

export const usePresence = () => {
  const userId = useAuthStore((state) => state.user?.id);
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const updatePresence = async (userId: string) => {
    try {
      await setDoc(
        doc(db, PRESENCE_COLLECTION, userId),
        { lastSeen: serverTimestamp() },
        { merge: true }
      );
    } catch (error) {
      // A heartbeat that cannot be written is not worth an unhandled rejection:
      // the worst case is that other people see this user as offline.
      console.warn('Could not update presence:', (error as Error).message);
    }
  };

  useEffect(() => {
    if (!userId) return;

    updatePresence(userId);

    intervalRef.current = setInterval(() => {
      if (document.visibilityState === 'visible') {
        updatePresence(userId);
      }
    }, HEARTBEAT_INTERVAL_MS);

    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        updatePresence(userId);
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      if (intervalRef.current) clearInterval(intervalRef.current);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [userId]);
};
